import React, { useMemo } from 'react';
import { EChart as ReactECharts } from '../EChart';
import { useAppContext } from '../../../providers/AppProvider';

const safeArr = v => (Array.isArray(v) ? v : []);
const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

function getOrderProductionStatus(order) {
  const lines = safeArr(order?.lines);

  const produced = lines.reduce((s, l) => s + num(l.producedLength), 0);
  const allocated = lines.reduce((s, l) => s + num(l.allocatedLength), 0);
  const total = lines.reduce((s, l) => s + num(l.totalOrderedLength), 0);

  if (produced > 0) return 'in_progress';
  if (allocated >= total && total > 0) return 'fully_allocated';
  if (allocated > 0) return 'partially_allocated';
  return 'not_allocated';
}

const LABELS = {
  not_allocated: 'Not allocated',
  partially_allocated: 'Partially allocated',
  fully_allocated: 'Fully allocated',
  in_progress: 'In progress'
};

const OrdersAllocationStatusChart = ({ orders = [], style }) => {
  const { getThemeColor } = useAppContext();

  const counts = useMemo(() => {
    const c = {
      not_allocated: 0,
      partially_allocated: 0,
      fully_allocated: 0,
      in_progress: 0
    };

    for (const o of orders || []) {
      const s = getOrderProductionStatus(o);
      c[s] = (c[s] || 0) + 1;
    }

    return c;
  }, [orders]);

  const total = useMemo(
    () => Object.values(counts).reduce((sum, v) => sum + (Number(v) || 0), 0),
    [counts]
  );

  const option = useMemo(
    () => ({
      color: [
        // Not allocated leads the ring, and it is the state that needs a
        // decision — the same danger the badges use, rather than a grey that
        // vanishes on a dark background.
        getThemeColor('danger-300'),
        getThemeColor('warning-300'),
        getThemeColor('info'),
        getThemeColor('success')
      ],
      tooltip: {
        trigger: 'item',
        padding: [8, 12],
        backgroundColor: getThemeColor('gray-soft'),
        borderColor: getThemeColor('gray-300'),
        borderWidth: 1,
        textStyle: {
          color: getThemeColor('dark'),
          fontFamily: 'Nunito Sans'
        },
        formatter: params =>
          `<strong>${params?.name || ''}</strong><br/>Count: <strong>${params?.value || 0}</strong>`
      },
      legend: {
        bottom: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: {
          color: getThemeColor('gray-800'),
          fontFamily: 'Nunito Sans',
          fontWeight: 600
        }
      },
      series: [
        {
          type: 'pie',
          radius: ['42%', '72%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          itemStyle: {
            borderColor: getThemeColor('body-bg'),
            borderWidth: 4
          },
          emphasis: {
            scale: true,
            scaleSize: 4,
            label: {
              show: true,
              formatter: '{b}\n{c}',
              fontWeight: 700,
              fontSize: 13,
              color: getThemeColor('gray-900')
            }
          },
          data: Object.keys(counts).map(k => ({
            name: LABELS[k] || k,
            value: counts[k] || 0
          }))
        }
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '36%',
          style: {
            text: String(total),
            textAlign: 'center',
            fill: getThemeColor('gray-1100'),
            fontSize: 28,
            fontWeight: 800,
            fontFamily: 'Nunito Sans'
          }
        },
        {
          type: 'text',
          left: 'center',
          top: '46%',
          style: {
            text: 'orders',
            textAlign: 'center',
            fill: getThemeColor('gray-700'),
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'Nunito Sans'
          }
        }
      ]
    }),
    [counts, total, getThemeColor]
  );

  return <ReactECharts option={option} style={style} />;
};

export default OrdersAllocationStatusChart;
